import { motion, AnimatePresence } from 'framer-motion';
import { Bot, Plus, Trash2, ChevronDown, RotateCcw } from 'lucide-react';
import { useState } from 'react';
import { MODELS, MODEL_KEYS, TIER_LABELS } from '../lib/models.js';

const AGENT_ACCENTS = [
  { ring: 'ring-accent-cyan/50', glow: 'shadow-glow-cyan', dot: 'bg-accent-cyan', text: 'text-accent-cyan-light' },
  { ring: 'ring-accent-orange/50', glow: 'shadow-glow-orange', dot: 'bg-accent-orange', text: 'text-accent-orange-soft' },
  { ring: 'ring-purple-400/40', glow: 'shadow-[0_0_0_1px_rgba(167,139,250,0.4),0_8px_32px_-8px_rgba(167,139,250,0.45)]', dot: 'bg-purple-400', text: 'text-purple-300' },
  { ring: 'ring-emerald-400/40', glow: 'shadow-[0_0_0_1px_rgba(52,211,153,0.4),0_8px_32px_-8px_rgba(52,211,153,0.45)]', dot: 'bg-emerald-400', text: 'text-emerald-300' },
];

export function AgentRoster({ agents, onChange, onAdd, onRemove, onResetSystemPrompts, maxTier, onMaxTierChange }) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.55, delay: 0.05, ease: [0.22, 1, 0.36, 1] }}
      className="panel p-6"
    >
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="section-title">
            <Bot className="h-4 w-4 text-accent-cyan" />
            Agents <span className="font-mono text-xs text-ink-dim">· {agents.length}</span>
          </div>
          <p className="mt-1 text-xs text-ink-dim">Each agent runs in parallel with its own model & system prompt.</p>
        </div>
        <div className="flex items-center gap-2">
          <TierSlider value={maxTier} onChange={onMaxTierChange} />
          <button onClick={onResetSystemPrompts} className="btn-ghost text-xs">
            <RotateCcw className="h-3 w-3" /> Reset prompts
          </button>
          <button onClick={onAdd} disabled={agents.length >= 6} className="btn-secondary text-xs">
            <Plus className="h-3.5 w-3.5" /> Add agent
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2 xl:grid-cols-3">
        <AnimatePresence initial={false}>
          {agents.map((a, i) => (
            <AgentCard
              key={a.id}
              index={i}
              agent={a}
              maxTier={maxTier}
              onChange={(patch) => onChange(i, patch)}
              onRemove={agents.length > 1 ? () => onRemove(i) : null}
            />
          ))}
        </AnimatePresence>
      </div>
    </motion.section>
  );
}

function TierSlider({ value, onChange }) {
  return (
    <div className="flex items-center gap-2 rounded-xl border border-border-subtle bg-bg-deep/60 px-3 py-1.5">
      <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-ink-dim">max_tier</span>
      <input
        type="range"
        min={1}
        max={5}
        step={1}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="h-1 w-24 cursor-pointer appearance-none rounded-full bg-border-strong accent-accent-cyan"
      />
      <span className="font-mono text-xs text-accent-cyan-light tabular-nums">
        T{value} · {TIER_LABELS[value]}
      </span>
    </div>
  );
}

function AgentCard({ index, agent, maxTier, onChange, onRemove }) {
  const accent = AGENT_ACCENTS[index % AGENT_ACCENTS.length];
  const m = MODELS[agent.model];
  const [open, setOpen] = useState(false);
  const allowed = MODEL_KEYS.filter((k) => MODELS[k].tier <= maxTier);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.96, y: 8 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.96, y: -4 }}
      transition={{ duration: 0.3 }}
      className={`panel relative flex flex-col gap-3 rounded-2xl p-4 ring-1 ${accent.ring}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className={`relative flex h-2.5 w-2.5 ${accent.dot} rounded-full`}>
            <span className={`absolute inset-0 animate-pulse-glow rounded-full ${accent.dot} opacity-50`} />
          </span>
          <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-ink-dim">Agent {index + 1}</span>
        </div>
        {onRemove && (
          <button onClick={onRemove} className="text-ink-dim transition hover:text-signal-err" title="Remove">
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      <div>
        <button
          onClick={() => setOpen((v) => !v)}
          className="flex w-full items-center justify-between rounded-xl border border-border-subtle bg-bg-deep/60 px-3 py-2 transition hover:border-accent-cyan/50"
        >
          <div className="flex items-center gap-2">
            <span className={`h-2 w-2 rounded-full`} style={{ background: m?.color || '#94a8c4' }} />
            <span className="font-medium text-ink-primary">{m?.label || agent.model}</span>
            <span className="font-mono text-[10px] text-ink-dim">T{m?.tier} · {m?.vendor}</span>
          </div>
          <ChevronDown className={`h-4 w-4 text-ink-dim transition ${open ? 'rotate-180' : ''}`} />
        </button>
        {open && (
          <motion.ul
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="scrollbar-stealth mt-2 max-h-72 overflow-y-auto rounded-xl border border-border-subtle bg-bg-deep/80"
          >
            {allowed.map((k) => {
              const md = MODELS[k];
              const sel = k === agent.model;
              return (
                <li key={k}>
                  <button
                    onClick={() => { onChange({ model: k }); setOpen(false); }}
                    className={`flex w-full items-center justify-between px-3 py-2 text-left text-sm transition ${sel ? 'bg-accent-cyan/10 text-accent-cyan-light' : 'text-ink-primary hover:bg-bg-surface2/60'}`}
                  >
                    <span className="flex items-center gap-2">
                      <span className="h-2 w-2 rounded-full" style={{ background: md.color }} />
                      <span className="font-medium">{md.label}</span>
                      <span className="font-mono text-[10px] text-ink-dim">T{md.tier}</span>
                    </span>
                    <span className="font-mono text-[10px] text-ink-dim">
                      ${md.inputPer1M}/${md.outputPer1M} per 1M
                    </span>
                  </button>
                </li>
              );
            })}
          </motion.ul>
        )}
      </div>

      <div>
        <label className="label mb-1.5">System prompt</label>
        <textarea
          value={agent.systemPrompt}
          onChange={(e) => onChange({ systemPrompt: e.target.value })}
          placeholder="You are a…"
          rows={3}
          className="input scrollbar-stealth text-sm"
        />
      </div>

      <div className="flex flex-wrap gap-1">
        {(m?.strengths || []).slice(0, 4).map((s) => (
          <span key={s} className={`chip text-[10px] ${accent.text}`} style={{ borderColor: 'rgba(255,255,255,0.08)' }}>
            {s}
          </span>
        ))}
      </div>
    </motion.div>
  );
}
